import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import cx from "classnames";
import { format, isValid } from "date-fns";
import {
  Input,
  SelectAutocomplete,
  AutoSaveField as Field,
  DatePicker
} from "../../../../components/Form";
import { getInvalidMessage, getRequiredMessage } from "../../../../utils/getValidationMessage";
import { ContinueButton } from "../../../../components/Buttons/ContinueButton";
import {
  NUMBER_REGEX,
  SPECIAL_CHARACTERS_REGEX,
  ALPHANUMERIC_REGEX,
  checkIsTrimmed
} from "../../../../utils/validation";
import { MAX_COMPANY_NAME_LENGTH, MAX_REGISTRATION_NUMBER_LENGTH } from "../../constants";
import { getMinDateMessage } from "../../../../utils/getValidationMessage";
import { DATE_FORMAT } from "../../../../constants";
import { useStyles } from "../styled";

const initialValues = {
  companyName: "",
  vatRegistrationNumber: "",
  noOfEmployees: "",
  companyCategory: "",
  corporateTaxid: "",
  corporateTaxIdRegDate: ""
};

const companyDetailsSchema = () =>
  Yup.object({
    companyName: Yup.string()
      .required(getRequiredMessage("Company name"))
      // eslint-disable-next-line no-template-curly-in-string
      .max(MAX_COMPANY_NAME_LENGTH, "Maximum ${max} characters allowed")
      .matches(SPECIAL_CHARACTERS_REGEX, getInvalidMessage("Company name"))
      .test("space validation", getInvalidMessage("Company name"), checkIsTrimmed),
    vatRegistrationNumber: Yup.string().matches(
      NUMBER_REGEX,
      getInvalidMessage("Registration number")
    ),
    noOfEmployees: Yup.string().required(getRequiredMessage("No of Employees")),
    companyCategory: Yup.string().required(getRequiredMessage("Company category")),
    corporateTaxId: Yup.string()
      .nullable()
      .notRequired()
      .max(20, "Corporate Tax ID must be at most 20 characters long")
      .matches(ALPHANUMERIC_REGEX, getInvalidMessage("Corporate Tax ID"))
      .test(
        "space validation",
        getInvalidMessage("Corporate Tax ID"),
        value => !value || value.trim() === value
      ),
    corporateTaxIdRegDate: Yup.date()
      .nullable()
      .notRequired()
      .min(new Date(1900, 0, 1), getMinDateMessage("Corporate Tax ID Registration Date"))
      .max(new Date(), getInvalidMessage("Corporate Tax ID Registration Date"))
      .typeError(getInvalidMessage("Corporate Tax ID Registration Date"))
  });

const changeDateProspectHandler = (_, value, path) =>
  /* istanbul ignore next */
  isValid(value) && { [path]: format(value, DATE_FORMAT) };

export const CompanyDetails = ({ handleContinue, createFormChangeHandler }) => {
  const classes = useStyles();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={companyDetailsSchema}
      validateOnChange={false}
      onSubmit={handleContinue}
    >
      {createFormChangeHandler(values => (
        <Form>
          <Grid container spacing={3}>
            <Grid item sm={6} xs={12}>
              <Field
                id="companyName"
                name="companyName"
                label="Company Name"
                path="prospect.organizationInfo.companyName"
                contextualHelpText="The company name given here will appear in all Bank records including Cheque Books. If the Company's name in Trade License is more than 50 characters long (including space), then an abbreviation can be used. Example If the company name is 'Airlift Global Automation and Heavy Equipment Rental LLC', mention the company name as 'Airlift Global Automation H E R'"
                infoTitle="These details should be the same as in your Trade License"
                component={Input}
                InputProps={{
                  inputProps: { maxLength: MAX_COMPANY_NAME_LENGTH, tabIndex: 0 }
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                name="companyCategory"
                label="Company Category"
                path="prospect.orgKYCDetails.companyCategory"
                contextualHelpText="Select Foreign / Offshore / Non-Resident company if applicable. In case of a Free Zone company  select Free Zone. In case of Civil Company select  Partnerships. Select appropriate category in all other cases"
                datalistId="companyCategory"
                component={SelectAutocomplete}
                tabIndex="0"
                isSearchable
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item sm={6} xs={12}>
              <Field
                id="vatRegistrationNumber"
                name="vatRegistrationNumber"
                label="VAT registration number (optional)"
                placeholder="123456789012345"
                path="prospect.organizationInfo.vatRegistrationNumber"
                infoTitle="This should be the same as your TRN number of UAE"
                component={Input}
                InputProps={{
                  inputProps: { maxLength: MAX_REGISTRATION_NUMBER_LENGTH, tabIndex: 0 }
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                id="noOfEmployees"
                name="noOfEmployees"
                label="Number of employees"
                path="prospect.organizationInfo.noOfEmployees"
                datalistId="noOfEmployee"
                component={SelectAutocomplete}
                filterOptions={options =>
                  options.sort((a, b) => {
                    const parseStart = text =>
                      text.startsWith(">")
                        ? parseInt(text.slice(1), 10) + 1
                        : parseInt(text.split("-")[0], 10) || Infinity;
                    return parseStart(a.displayText) - parseStart(b.displayText);
                  })
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item sm={6} xs={12}>
              <Field
                id="corporateTaxId"
                name="corporateTaxId"
                label="Corporate Tax ID (optional)"
                placeholder="Enter Corporate Tax ID"
                path="prospect.organizationInfo.corporateTaxId"
                component={Input}
                InputProps={{
                  inputProps: { maxLength: 20, tabIndex: 0 }
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Field
                id="corporateTaxIdRegDate"
                name="corporateTaxIdRegDate"
                label="Corporate Tax ID Registration Date"
                path="prospect.organizationInfo.corporateTaxIdRegDate"
                component={DatePicker}
                InputProps={{
                  inputProps: { tabIndex: 0, "data-testid": "corporateTaxIdRegDate" }
                }}
                changeProspect={changeDateProspectHandler}
              />
            </Grid>
          </Grid>
          <Grid
            className={cx(classes.continueButton, classes.detailsContinueButton)}
            container
            direction="row"
            justify="flex-end"
          >
            <ContinueButton type="submit" />
          </Grid>
        </Form>
      ))}
    </Formik>
  );
};
